import {
  Box,
  Checkbox,
  CollapsibleSection,
  Flex,
  Icon,
  InlineLink,
  Pane,
  Text,
  Tooltip,
} from '@fullstory/components';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { useFlags } from '@fullstory/react-hooks';
import { NamedElementCaptureOptions } from '@fullstory/redux';
import { getOrgId } from '@fullstory/redux/modules/fullstory/selectors';
import { generateRoutePath, PLAN_UPGRADE_ROUTE } from '@fullstory/routes';
import React, { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import css from './index.module.scss';

interface DataCaptureOptionsProps extends NamedElementCaptureOptions {
  setWatched: (value: boolean) => void;
  setRageClicksBlocked: (value: boolean) => void;
  setDeadClicksBlocked: (value: boolean) => void;
  disabled?: boolean;
  autoWatch?: boolean;
}

export function DataCaptureOptions({
  watched,
  setWatched,
  rageClicksBlocked,
  setRageClicksBlocked,
  deadClicksBlocked,
  setDeadClicksBlocked,
  disabled = false,
  autoWatch = false,
}: DataCaptureOptionsProps) {
  const intl = useIntl();
  const flags = useFlags();
  const orgId = useSelector(getOrgId);
  const watchedElemsEnabled = flags.hasBit(Flag.FeatWatchedElems);
  const infoTooltipContent = intl.formatMessage(
    {
      defaultMessage:
        'Watched Elements let you determine if your users saw an error message, promo banner, or any CSS element that was rendered on the page - even if they didn’t engage with it. <b>Learn more</b>',
      id: 'axI8Bm',
    },
    {
      b(chunks: ReactNode) {
        return (
          <InlineLink
            variant="inverse"
            href="https://help.fullstory.com/hc/en-us/articles/360034693073-Watched-Elements-Can-I-search-for-CSS-elements-without-a-click-event-"
            target="_new"
          >
            {chunks}
          </InlineLink>
        );
      },
    },
  );
  const lockTooltipContent = intl.formatMessage(
    {
      defaultMessage:
        'Watched Elements aren’t included in your current plan. <b>View plans</b> and learn how to add them.',
      id: 'UGenB1',
    },
    {
      b(chunks: ReactNode) {
        return (
          <InlineLink
            variant="inverse"
            to={generateRoutePath(PLAN_UPGRADE_ROUTE, {
              orgId,
            })}
          >
            {chunks}
          </InlineLink>
        );
      },
    },
  );

  return (
    <Flex data-component="DataCaptureOptions" column alignItems="flex-start">
      <CollapsibleSection initiallyCollapsed={!(autoWatch && watchedElemsEnabled && !disabled)}>
        <Box marginBottom={8}>
          <CollapsibleSection.Header>
            <Box marginRight={4}>
              <CollapsibleSection.Icon size={16} />
            </Box>
            <Text appearance="bodyMedium" color="emphasized">
              <FormattedMessage defaultMessage="Capture Options" id="nGiti6" />
            </Text>
          </CollapsibleSection.Header>
        </Box>
        <CollapsibleSection.Panel>
          <Pane
            backgroundColor="accent-warning"
            padding={8}
            marginBottom={16}
            width="100%"
            borderRadius={4}
            className={css.warningBanner}
          >
            <Flex justifyContent="flex-start">
              <Box marginRight={12}>
                <Icon name="warning" color="default" size={16} />
              </Box>
              <Text appearance="bodyMedium">
                <FormattedMessage
                  defaultMessage="Please note that changes to element capture options are applied to future sessions."
                  id="5dG+Di"
                />
              </Text>
            </Flex>
          </Pane>
          <Box marginBottom={16}>
            <Flex column alignItems="flex-start">
              <Box marginBottom={8} maxWidth={550}>
                <Text appearance="bodyMedium" color="emphasized">
                  <FormattedMessage defaultMessage="Watch rule" id="DuH6Jo" />
                </Text>
              </Box>
              <Box>
                <Flex gap={4}>
                  <Checkbox
                    testId="watch-element-checkbox"
                    label={intl.formatMessage({
                      defaultMessage: 'Watch this Element',
                      id: 'AflqtT',
                    })}
                    checked={watched}
                    onChange={setWatched}
                    disabled={!watchedElemsEnabled || disabled}
                  />
                  <Tooltip
                    interactive
                    maxWidth={284}
                    testId="watchedElementInfo"
                    tooltipContent={infoTooltipContent}
                  >
                    <Icon name="information" color="secondary" />
                  </Tooltip>
                  {!watchedElemsEnabled && (
                    <Tooltip
                      interactive
                      maxWidth={284}
                      testId="watchedElementLock"
                      tooltipContent={lockTooltipContent}
                    >
                      <Icon name="lock" color="secondary" />
                    </Tooltip>
                  )}
                </Flex>
              </Box>
            </Flex>
          </Box>
          <Flex column alignItems="flex-start">
            <Box marginBottom={8} maxWidth={550}>
              <Flex column alignItems="flex-start">
                <Text appearance="bodyMedium" color="emphasized">
                  <FormattedMessage defaultMessage="Frustration signals" id="kDOD5p" />
                </Text>
                <Text appearance="bodyMedium" color="secondary">
                  <FormattedMessage
                    defaultMessage="Prevent this element from being classified as a:"
                    id="p6Uanm"
                  />
                </Text>
              </Flex>
            </Box>
            <Flex column justifyContent="flex-start">
              <Box marginBottom={8}>
                <Checkbox
                  testId="rage-click-element-checkbox"
                  label={intl.formatMessage({ defaultMessage: 'Rage Click', id: 'PSUo53' })}
                  checked={rageClicksBlocked}
                  onChange={setRageClicksBlocked}
                  disabled={disabled}
                />
              </Box>
              <Box marginBottom={0}>
                <Checkbox
                  testId="dead-click-element-checkbox"
                  label={intl.formatMessage({ defaultMessage: 'Dead Click', id: 'hsHG36' })}
                  checked={deadClicksBlocked}
                  onChange={setDeadClicksBlocked}
                  disabled={disabled}
                />
              </Box>
            </Flex>
          </Flex>
        </CollapsibleSection.Panel>
      </CollapsibleSection>
    </Flex>
  );
}
